import { apiClient } from './api';
import { publicBookingApiParamsFromClubRouteKey } from '../utils/club-route-key';
import type { CustomerType } from './customer-types';

export interface GolfClub {
  id: string;
  name: string;
  description?: string;
  address: string;
  contactInfo: string;
  phone?: string;
  openingHours: string;
  amenities: string;
  policies?: string;
  cancellationWindowHours?: number;
  cancellationPolicyHours?: number;
  status: string;
  defaultGuestTypeId?: string;
  createdAt: string;
  updatedAt: string;
  clubNumber?: number;
  courses?: GolfCourse[];
  defaultGuestType?: CustomerType;
}

export interface GolfCourse {
  id: string;
  name: string;
  description?: string;
  clubId: string;
  numberOfHoles: number;
  par: number;
  yardage: number;
  rating?: number;
  slope?: number;
  layoutMapUrl?: string;
  color: string; // Course color for identification
  status: string;
  onlineBooking?: boolean;
  /** When true, admin "Mandatory Carts" — half cart pre-loaded with green fee for every player. */
  preloadHalfCartWithGreenFee?: boolean;
  defaultGuestTypeId?: string; // Default customer type for new reservations
  createdAt: string;
  updatedAt: string;
  club?: GolfClub;
  defaultGuestType?: CustomerType;
  teeSheetTemplates?: TeeSheetTemplate[];
}

export interface TeeSheetTemplate {
  id: string;
  name: string;
  description?: string;
  startTime: string;
  endTime: string;
  intervalMinutes: number;
  holes: number;
  routingSequence?: string;
  isShotgun: boolean;
  blockedTimes?: string;
  maxGolfersPerSlot: number;
  createdAt: string;
  updatedAt: string;
}

function unwrapGolfClubsList(
  response: { success?: boolean; data?: GolfClub[] } | GolfClub[]
): GolfClub[] {
  if (Array.isArray(response)) return response;
  if (response && typeof response === 'object' && Array.isArray((response as { data?: GolfClub[] }).data)) {
    return (response as { data: GolfClub[] }).data;
  }
  return [];
}

export const golfClubsService = {
  // Get all golf clubs - Note: This endpoint requires staff auth; customers should use public club/course APIs.
  getAll: async () => {
    const response = await apiClient.get<{ success?: boolean; data?: GolfClub[] } | GolfClub[]>('/golf-clubs');
    return unwrapGolfClubsList(response);
  },

  // Get golf club by ID
  getById: (id: string) => {
    return apiClient.get<GolfClub>(`/golf-clubs/${id}`);
  },

  // Create golf club
  create: (data: Omit<GolfClub, 'id' | 'createdAt' | 'updatedAt'>) => {
    return apiClient.post<GolfClub>('/golf-clubs', data);
  },

  // Update golf club
  update: (id: string, data: Partial<GolfClub>) => {
    return apiClient.put<GolfClub>(`/golf-clubs/${id}`, data);
  },

  // Delete golf club
  delete: (id: string) => {
    return apiClient.delete<void>(`/golf-clubs/${id}`);
  },

  // Get all courses
  getCourses: () => {
    // Add cache-busting parameter to prevent browser caching
    const params = new URLSearchParams();
    params.append('_t', Date.now().toString());
    return apiClient.get<GolfCourse[]>(`/golf-clubs/courses?${params.toString()}`);
  },

  // Get course by ID
  getCourseById: (id: string) => {
    return apiClient.get<GolfCourse>(`/golf-clubs/courses/${id}`);
  },

  // Create course
  createCourse: (data: Omit<GolfCourse, 'id' | 'createdAt' | 'updatedAt'>) => {
    return apiClient.post<GolfCourse>('/golf-clubs/courses', data);
  },

  // Update course
  updateCourse: (id: string, data: Partial<GolfCourse>) => {
    return apiClient.put<GolfCourse>(`/golf-clubs/courses/${id}`, data);
  },

  // Delete course
  deleteCourse: (id: string) => {
    return apiClient.delete<void>(`/golf-clubs/courses/${id}`);
  },

  // Get courses with templates - ALWAYS uses public endpoint (no auth required)
  getCoursesWithTemplates: (clubRouteKey?: string | null) => {
    console.log('🔍 golfClubsService.getCoursesWithTemplates called with clubRouteKey:', clubRouteKey);
    const params = publicBookingApiParamsFromClubRouteKey(clubRouteKey);
    const queryString = params.toString();
    // Always use public endpoint - booking service doesn't have admin auth
    const url = `/golf-clubs/public/courses-with-templates${queryString ? '?' + queryString : ''}`;
    return apiClient.get<GolfCourse[]>(url);
  },

  // GET /golf-clubs returns { success, data: clubs }; unwrap to match admin web app behavior.
  getClubInfo: async () => {
    const response = await apiClient.get<{ success?: boolean; data?: GolfClub[] } | GolfClub[]>('/golf-clubs');
    return unwrapGolfClubsList(response);
  }
}; 